// Task Detail — a task é uma issue do Linear.
// Rota: #/tasks/<identificador>, ex. #/tasks/BYM-1583.
//
// Fora desta tela, porque o MCP do Linear não expõe (não por preguiça):
// - Sub-issues e dependências: `list_issues` não filtra por pai e não há
//   ferramenta de relação. Volta quando o servidor expuser.
// - Runs/custo por task: dependia da chave efêmera `task-<numero>` do GitHub.

import { empty, errorBox, esc, head, md, pill, skeleton, toast } from "../ui";
import { linear, SourceError, type LinearIssue } from "../api";

// ------------------------------------------------------------------- estilo

const CSS = `
.task-col{max-width:780px;display:flex;flex-direction:column;gap:14px}
.task-badge{display:inline-block;font-size:11px;background:var(--surface-2);color:var(--ink-2);border-radius:9px;padding:1px 7px;font-family:var(--mono)}
.task-grid{display:grid;grid-template-columns:1fr 1fr;gap:0 28px;align-items:start}
.task-l{color:var(--ink-3);font-size:12px;margin:10px 0 3px}
.task-l:first-child{margin-top:0}
.task-v{font-weight:600}
.task-pills{display:flex;gap:6px;flex-wrap:wrap}
.task-inline{display:flex;gap:8px;align-items:center}
.task-inline .input{flex:1}
.task-clamp{max-height:220px;overflow:hidden}
.task-clamp.task-open{max-height:none}
.task-more{background:none;border:none;color:var(--accent);cursor:pointer;padding:4px 0;font:inherit;font-size:12px}
.task-entry{padding:10px 0;border-bottom:1px solid var(--rule-2)}
.task-data{color:var(--ink-3);font-size:12px;margin-left:6px}
.task-comment{margin-top:12px}
`;

function injectCss(): void {
  if (!document.getElementById("css-task-detail")) {
    const st = document.createElement("style");
    st.id = "css-task-detail";
    st.textContent = CSS;
    document.head.append(st);
  }
}

// ----------------------------------------------------------------- helpers

type Comentario = { id: string; body: string; user?: string; createdAt?: string };

const dataCurta = (iso: string): string =>
  new Date(iso).toLocaleString("en-US", { month: "short", day: "numeric" });

const badge = (n: number): string => (n ? `<span class="task-badge">${n}</span>` : "");

/** Tom do pill a partir do `statusType` do Linear — os tipos são dele
 *  (triage/backlog/unstarted/started/completed/canceled), então o mapa é
 *  fechado e não depende do NOME da coluna, que o time pode renomear. */
const tomDoStatus = (tipo: string | undefined): "ok" | "run" | "crit" | "idle" =>
  tipo === "completed" ? "ok" : tipo === "started" ? "run" : tipo === "canceled" ? "crit" : "idle";

// checagem do mapa de tom — roda uma vez em dev, silenciosa se ok
if (import.meta.env?.DEV) {
  console.assert(
    tomDoStatus("completed") === "ok" &&
      tomDoStatus("started") === "run" &&
      tomDoStatus("canceled") === "crit" &&
      tomDoStatus("backlog") === "idle" &&
      tomDoStatus(undefined) === "idle",
    "tomDoStatus: mapa de statusType quebrou",
  );
}

// ------------------------------------------------------------------- cards

function detailsHtml(issue: LinearIssue): string {
  const labels = issue.labels?.length
    ? `<div class="task-pills">${issue.labels.map((l) => pill(l)).join("")}</div>`
    : '<div class="task-v">—</div>';

  return `
    <div class="card">
      <div class="card-head"><span class="card-title">Details</span></div>
      <div class="card-body task-grid">
        <div>
          <div class="task-l">Status</div>
          <div>${pill(issue.status ?? "—", tomDoStatus(issue.statusType))}</div>
          <div class="task-l">Project</div>
          <div class="task-v">${esc(issue.project ?? "—")}</div>
          <div class="task-l">Labels</div>
          ${labels}
        </div>
        <div>
          <div class="task-l">Assignee</div>
          <div class="task-v">${esc(issue.assignee ?? "Unassigned")}</div>
          <div class="task-l">Priority</div>
          <div class="task-v">${esc(issue.priority?.name ?? "—")}</div>
          ${issue.estimate == null ? "" : `<div class="task-l">Estimate</div><div class="task-v">${esc(issue.estimate)}</div>`}
          ${issue.parentId ? `<div class="task-l">Parent</div><div class="task-v"><a href="#/tasks/${esc(issue.parentId)}">${esc(issue.parentId)}</a></div>` : ""}
        </div>
      </div>
    </div>`;
}

function descricaoHtml(issue: LinearIssue): string {
  return `
    <div class="card">
      <div class="card-head"><span class="card-title">Description</span></div>
      <div class="card-body">
        ${issue.description ? `<div class="task-clamp">${md(issue.description)}</div>` : '<p class="hint">Sem descrição — a issue do Linear está vazia.</p>'}
      </div>
    </div>`;
}

function comentariosHtml(comments: Comentario[]): string {
  const entradas = comments
    .map(
      (c) => `
      <div class="task-entry">
        <div><strong>${esc(c.user ?? "—")}</strong>${c.createdAt ? `<span class="task-data">${esc(dataCurta(c.createdAt))}</span>` : ""}</div>
        <div class="task-clamp">${md(c.body)}</div>
      </div>`,
    )
    .join("");
  return `
    <div class="card">
      <div class="card-head"><span class="card-title">Comments</span>${badge(comments.length)}</div>
      <div class="card-body">
        ${entradas || '<p class="hint">Nenhum comentário ainda — o agente escreve o progresso aqui.</p>'}
        <form id="task-comentar" class="task-inline task-comment">
          <input class="input" name="body" placeholder="Add a comment..." autocomplete="off" />
          <button class="btn btn-primary" type="submit" title="Enviar">➤</button>
        </form>
      </div>
    </div>`;
}

/** Show more só onde o conteúdo de fato estoura o card. */
function clamps(raiz: HTMLElement): void {
  raiz.querySelectorAll<HTMLElement>(".task-clamp").forEach((c) => {
    if (c.scrollHeight <= c.clientHeight + 4) return;
    const b = document.createElement("button");
    b.type = "button";
    b.className = "task-more";
    b.textContent = "Show more ▾";
    b.addEventListener("click", () => {
      b.textContent = c.classList.toggle("task-open") ? "Show less ▴" : "Show more ▾";
    });
    c.after(b);
  });
}

// ------------------------------------------------------------------- página

export async function taskDetail(el: HTMLElement): Promise<void> {
  injectCss();
  const id = decodeURIComponent(location.hash.split("/")[2] ?? "").toUpperCase();
  if (!/^[A-Z][A-Z0-9]*-\d+$/.test(id)) {
    el.innerHTML =
      head({ title: "Task" }) +
      empty("Task não encontrada", "A rota é #/tasks/<identificador>, como BYM-1583 — abra uma task pela lista.");
    return;
  }

  el.innerHTML = head({ title: id }) + skeleton(4);

  let issue: LinearIssue;
  let comments: Comentario[];
  try {
    // Duas ferramentas do mesmo MCP; sem autorização do `linear_oficial` as
    // duas falham juntas, e é o erro da origem que a tela mostra.
    [issue, comments] = await Promise.all([linear.issue(id), linear.comments(id)]);
  } catch (e) {
    const err = e as SourceError;
    el.innerHTML =
      head({ title: id }) +
      errorBox(err.source ?? "Linear", err.message, err.next ?? "confira a conexão do servidor no LiteLLM");
    return;
  }

  el.innerHTML =
    head({
      title: `${issue.id} ${issue.title}`,
      actions: pill(issue.status ?? "—", tomDoStatus(issue.statusType)),
    }) +
    `<div class="task-col">` +
    detailsHtml(issue) +
    descricaoHtml(issue) +
    `<div id="task-comments">${comentariosHtml(comments)}</div>` +
    `</div>`;

  clamps(el);

  // ---- Comments: comentar sem recarregar a página
  const ligarForm = (): void => {
    el.querySelector<HTMLFormElement>("#task-comentar")!.addEventListener("submit", async (e) => {
      e.preventDefault();
      const form = e.target as HTMLFormElement;
      const body = String(new FormData(form).get("body") ?? "").trim();
      if (!body) return toast("Escreva o comentário antes de enviar");
      const botao = form.querySelector("button")!;
      botao.disabled = true;
      try {
        await linear.comenta(id, body);
        comments = await linear.comments(id);
        const cont = el.querySelector<HTMLElement>("#task-comments")!;
        cont.innerHTML = comentariosHtml(comments);
        clamps(cont);
        ligarForm();
        toast("Comentário publicado na issue");
      } catch (err) {
        toast((err as Error).message);
        botao.disabled = false;
      }
    });
  };
  ligarForm();
}
